import { ChangeDetectorRef, Component, EventEmitter, OnInit, OnDestroy, Output, ElementRef, Input, AfterViewInit, ViewChild } from '@angular/core';

import { Observable, of, Subscription  } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { MessageService } from 'primeng/api';

import { allowedFileTypes, FileUploadModel } from '../../../../Common/Forms/models/file-upload.model';
import { CsvMentionApiModel } from '../../../../api/mentions/model/csv-mention-api.model';
import {
    CsvField, FallbackInputTypeEnum, ImportedFile
} from '../../../../Common/Dialogs/components/CsvImportDialog/csv-import-dialog.service';
import {
    MentionCsvImportDialogService,
    MentionImportFileParameters,
    MentionImportParameters,
    MentionImportResponseModel,
    MentionImportTableFields,
    MentionImportToCollectionEnum
} from '../../services/mention-csv-import-dialog.service';
import { CsvImporterComponent } from '../../../../Common/TargetingShared/components/CsvImporter/csv-importer.component';
import { DropdownOptionModel } from '../../../../Common/Forms/models/dropdown-option.model';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { MentionImportConfirmationComponent } from './MentionImportConfirmation/mention-import-confirmation.component';
import { Folder } from '../../models/folder.model';
import { FolderService } from '../../../Core/services/folder.service';
import { TagModel } from '../../../../Common/Tags/models/tag.model';
import { TagsService } from '../../../../Common/Tags/services/tags.service';
import { ConfirmDialogComponent } from '../../../../Common/Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { AppearCondition, AutocompleteCustomOption } from '../../../../Common/Forms/components/Autocomplete/autocomplete-custom-option.model';
import { FocusHelper } from '../../../../Common/Forms/helpers/focus.helper';
import { MentionImportTypesEnum } from '../../../../Common/MonitorShared/enum/mention-import-types.enum';
import { MonitorConfigService } from '../../../../Common/MonitorShared/services/monitor-config.service';
import { MentionSearchService } from '../../../../Monitor/Core/services/mention-search.service';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { MentionImportArticle, NewsdeskMentionImportService } from './newsdesk-mention-import.service';
import { FileUploadComponent } from '../../../../Common/Forms/components/FileUpload/file-upload.component';
import { MeltwaterMentionImportService } from './meltwater-mention-import.service';
import { ProgressModel } from '@Common/Dialogs/models/progress.model';
import { userPermissionsEnum } from '../../../../Core/Users/enums/user-permissions.enum';
import { MentionCsvImportEmailReportService } from '@Monitor/Shared/services/mention-csv-import-email-report.service';
import { ResourceActionTypes } from '../../../../Common/Basic/enums/resource-actions.enum';

@Component({
    selector: 'agpr-mention-importer',
    templateUrl: './mention-importer.component.html',
    styleUrls: ['./mention-importer.component.scss'],
    providers: [
        MeltwaterMentionImportService,
        NewsdeskMentionImportService,
        MentionCsvImportEmailReportService
    ],
    standalone: false
})
// eslint-disable-next-line max-len
export class MentionImporterComponent extends CsvImporterComponent<CsvMentionApiModel, MentionImportFileParameters> implements OnInit, AfterViewInit, OnDestroy {

    @Input() set importMentionFolder(selectedFolder: Folder) {
        if (selectedFolder) {
            this.importFilesToCollection(selectedFolder, MentionImportToCollectionEnum.folders);
        }
    };

    @Output() hideMentionImporter: EventEmitter<void> = new EventEmitter<void>();

    loading: boolean = false;
    overlayMode: boolean = true;
    selectedImportType: DropdownOptionModel = null;
    selectedFiles: File[] = [];
    parameters: MentionImportParameters = new MentionImportParameters();
    noReportEmailError: boolean = false;
    fileParameters: MentionImportFileParameters = new MentionImportFileParameters();
    reportDestinationEmail: string = '';
    hasValidMentions: boolean = true;
    hasImportToMercury: boolean = false;

    folderList$: Observable<Folder[]>;
    tagList$: Observable<TagModel[]>;

    progress: ProgressModel;

    dropdownFolder: Folder = null;
    dropdownTag: TagModel = null;

    importTypeOptions: DropdownOptionModel[] = [];

    folderAutocompleteOptions: AutocompleteCustomOption[] = [
        {
            label: 'Mention.editDialog.folder.createNewFolder',
            action: (folderName: string) => {
                const folder = new Folder();
                folder.name = folderName;

                this.folderService.create(folder).pipe(
                    switchMap(id => this.folderList$.pipe(
                        map(folders => folders.find(x => x.id === id))
                    )),
                    take(1)
                ).subscribe(newFolder => {
                    this.importFilesToCollection(newFolder, MentionImportToCollectionEnum.folders);
                });
            },
            appearCondition: AppearCondition.noExactMatchInSuggestions,
            clearOnSelect: true
        }
    ];

    tagAutocompleteOptions: AutocompleteCustomOption[] = [
        {
            label: 'Tags.createTag',
            action: (searchString: string) => {
                const tagModel = new TagModel();
                tagModel.name = searchString;
                this.tagsService.createTagWithoutFetch(tagModel).pipe(
                    switchMap((id) => this.tagsService.refreshAllManual().pipe(
                        switchMap(() => {
                            return this.tagsService.getTagById(id);
                        }),
                    ))
                ).subscribe(tag => {
                    if (tag) {
                        this.importFilesToCollection(tag, MentionImportToCollectionEnum.tags);
                    }
                });
            },
            appearCondition: AppearCondition.noExactMatchInSuggestions,
            clearOnSelect: true
        }
    ];

    readonly importTypes = MentionImportTypesEnum;
    readonly fallbackInputTypes = FallbackInputTypeEnum;
    readonly messageSeverities = messageSeverityEnum;
    readonly importToCollectionTypes = MentionImportToCollectionEnum;
    readonly buttonTypes = ButtonTypesEnum;

    // eslint-disable-next-line @typescript-eslint/naming-convention
    readonly FILE_LIMIT = 1;
    readonly csvFileTypes = [
        ...allowedFileTypes.csvImportMIMETypesRegex.split('|').map(x => x[x.length - 1] === '$' ? x.substring(0, x.length - 1) : x),
    ].join(',');
    readonly xmlFileTypes = [
        ...allowedFileTypes.xmlMIMETypesRegex.split('|').map(x => x[x.length - 1] === '$' ? x.substring(0, x.length - 1) : x),
    ].join(',');
    readonly htmlFileTypes = [
        ...allowedFileTypes.htmlMIMETypesRegex.split('|').map(x => x[x.length - 1] === '$' ? x.substring(0, x.length - 1) : x),
    ];
    readonly fileTypesMap: Map<MentionImportTypesEnum, any> = new Map<MentionImportTypesEnum, any>([
        [
            MentionImportTypesEnum.GENERIC_CSV,
            {
                fileTypesRegex: allowedFileTypes.csvImportMIMETypesRegex,
                fileTypes: this.csvFileTypes,
                validationFunction: this.validateFileEncoding
            }
        ],
        [
            MentionImportTypesEnum.NEWSDESK,
            {
                fileTypesRegex: allowedFileTypes.xmlMIMETypesRegex,
                fileTypes: this.xmlFileTypes
            }
        ],
        [
            MentionImportTypesEnum.CISION,
            {
                fileTypesRegex: allowedFileTypes.csvImportMIMETypesRegex,
                fileTypes: this.csvFileTypes,
                validationFunction: this.validateFileEncoding
            }
        ],
        [
            MentionImportTypesEnum.MELTWATER,
            {
                fileTypesRegex: allowedFileTypes.htmlMIMETypesRegex,
                fileTypes: this.htmlFileTypes,
                validationFunction: this.validateFileEncoding
            }
        ]
    ]);

    private subscriptions: Subscription[] = [];
    private mentionImportRequest: Subscription;

    @ViewChild('fileUpload') fileUpload: FileUploadComponent;

    constructor(
        public coreDialogService: CoreDialogService,
        public csvImportDialogService: MentionCsvImportDialogService,
        public meltwaterMentionImportService: MeltwaterMentionImportService,
        public newsdeskMentionImportService: NewsdeskMentionImportService,
        public mentionCsvImportReportEmailService: MentionCsvImportEmailReportService,
        public messageService: MessageService,
        public localizationService: LocalizationService,
        public currentUserService: CurrentUserStateService,
        public folderService: FolderService,
        public tagsService: TagsService,
        public monitorConfigService: MonitorConfigService,
        public mentionSearchService: MentionSearchService,
        public cd: ChangeDetectorRef,
        private el: ElementRef,
        private currentUserStateService: CurrentUserStateService,
    ) {
        super(
            csvImportDialogService,
            coreDialogService,
            messageService,
            localizationService,
            cd
        );
        this.csvImportDialogService.initialize();
        this.folderList$ = this.folderService.all$;
        this.tagList$ = this.tagsService.all$;
    }

    ngOnInit(): void {
        this.subscriptions.push(
            this.csvImportDialogService.importedFiles$.subscribe(x => {
                this.csvFiles = x;
                this.updateDropdownOptions();
                this.updateFileParameters();
            }),
            this.csvImportDialogService.getProgress().subscribe(progress => {
                this.progress = progress;
            }),
            this.meltwaterMentionImportService.getProgress().subscribe(progress => {
                this.progress = progress;
            }),
            this.newsdeskMentionImportService.getProgress().subscribe(progress => {
                this.progress = progress;
            }),
            this.currentUserService.getUser().subscribe(user => {
                this.reportDestinationEmail = user.email;
                this.parameters.reportDestinationEmails.push(this.reportDestinationEmail);
            }),
            this.monitorConfigService.getMentionImportTypes().subscribe(mentionImportTypes => {
                mentionImportTypes.forEach(type => {
                    this.importTypeOptions.push(new DropdownOptionModel(`Monitoring.mentionImporter.fileType.${type}`, type));
                });
                this.importTypeOptions.sort((a, b) => this.localizationService.transform(a.label)
                    .localeCompare(this.localizationService.transform(b.label)));
            }),
            this.currentUserStateService.getPermissions().pipe(filter(x => !!x)).subscribe(permissions => {
                this.hasImportToMercury = permissions?.includes(userPermissionsEnum.csvImportToMercury);
            })
        );
    }

    ngAfterViewInit(): void {
        requestAnimationFrame(() => {
            const title: HTMLElement = document.querySelector('.agpr-mentionImporter-header-main');
            title?.focus();
        });
    }


    filesAdded(addedFiles: FileUploadModel[]): void {
        const filesToAdd = addedFiles.map(file => file.file);
        if (filesToAdd.length + this.selectedFiles.length > this.FILE_LIMIT) {
            this.messageService.add({
                severity: messageSeverityEnum.error,
                detail: this.localizationService.transform(
                    'Discover.csvImporter.errors.tooManyFiles',
                    { files: this.FILE_LIMIT }
                )
            });
            return;
        }
        this.selectedFiles.push(...filesToAdd);
        this.processFiles(filesToAdd);
    }

    fileRemoved(removedFile: FileUploadModel): void {
        const fileIndex = this.selectedFiles.findIndex(file => file.name === removedFile.name);
        this.selectedFiles.splice(fileIndex, 1);
        switch (this.selectedImportType.value) {
            case MentionImportTypesEnum.NEWSDESK:
                this.newsdeskMentionImportService.removeArticles(removedFile.file);
                break;
            case MentionImportTypesEnum.MELTWATER:
                this.meltwaterMentionImportService.removeArticles(removedFile.file);
                break;
        }
        this.hasValidMentions = true;
    }

    processFiles(files: File[]): void {
        switch (this.selectedImportType.value) {
            case MentionImportTypesEnum.NEWSDESK:
                files.forEach((file, index) => {
                    this.newsdeskMentionImportService.validateFile(file).subscribe(articles => {
                        if (!!articles) {
                            this.newsdeskMentionImportService.addArticles(articles as MentionImportArticle[]);
                        } else {
                            this.fileUpload.removeFile(index);
                        }
                    });
                });
                break;
            case MentionImportTypesEnum.MELTWATER:
                files.forEach((file, index) => {
                    this.meltwaterMentionImportService.addArticles(file).subscribe({
                        error: (error) => this.fileUpload.removeFile(index)
                    });
                });
                break;
        }
    }

    fileValidationErrors(files: FileUploadModel[]) {
        const fileNames = files.map(file => file.name);
        this.showFailedFilesDialog(fileNames);
    }

    closeImporter(): void {
        this.resetMentionImport();
        this.hideMentionImporter.emit();
    }

    resetImportTypes(): void {
        // import button not clicked
        if (this.fileUpload?.value) {
            for (let i = 0; i < this.fileUpload.value.length; i++) {
                this.fileUpload.removeFile(0);
            }
        }
        // on mapping page
        if (!this.fileUpload && this.csvFiles.length > 0) {
            this.csvImportDialogService.clearImportedFiles();
        }
    }

    resetMentionImport(): void {
        this.resetImportTypes();
        this.parameters = new MentionImportParameters();
        this.parameters.reportDestinationEmails.push(this.reportDestinationEmail);
        this.fileParameters = new MentionImportFileParameters();
        this.selectedImportType = null;
        this.noReportEmailError = false;
        this.csvFiles = [];
        this.selectedFiles = [];
    }

    importMentions(): void {
        switch (this.selectedImportType.value) {
            case MentionImportTypesEnum.GENERIC_CSV:
            case MentionImportTypesEnum.CISION:
                this.csvImportDialogService.setFieldDefinitions(this.selectedImportType.value);
                this.readFilesToCsvDialogService(this.selectedFiles, true);
                break;
            case MentionImportTypesEnum.NEWSDESK:
                const articlesLength = this.newsdeskMentionImportService.getArticlesLength();
                if (articlesLength > 0) {
                    this.newsdeskMentionImportService.setMentionsFromRawArticles();
                    this.showConfirmImportDialog();
                } else {
                    this.hasValidMentions = false;
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        detail: this.localizationService.transform('Monitoring.mentionImporter.importButton.noValidMentions')
                    });
                }
                break;
            case MentionImportTypesEnum.MELTWATER:
                this.meltwaterMentionImportService.setMentionsFromRawArticles();
                if (this.meltwaterMentionImportService.getArticlesLength() > 0) {
                    this.showConfirmImportDialog();
                } else {
                    this.hasValidMentions = false;
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        detail: this.localizationService.transform('Monitoring.mentionImporter.importButton.noValidMentions')
                    });
                }
                break;
            default:
                this.showConfirmImportDialog();
        }
    }

    importCsv(): void {
        let errorMessage = '';
        this.csvFiles.forEach(file => {
            file.csvFields.forEach(field => {
                if (field.originalField.required && (!field.targetColumn || !field.fallbackValue)) {
                    field.requiredError = true;
                    errorMessage = errorMessage ||
                    this.localizationService.transform('Monitoring.mentionImporter.fieldLabel.' + field.originalField.fieldId)
                    + this.localizationService.transform('Monitoring.mentionImporter.fieldRequired');
                }
                if (this.isDateTimeField(field) && field.targetColumn) {
                    if (
                        field.originalField.dateValidationFormatDropdownOptions
                        && (!field.selectedDateValidationFormat || field.selectedDateValidationFormat.value === null)
                    ) {
                        field.dateValidationFormatRequiredError = true;
                        errorMessage = errorMessage || this.localizationService.transform('Monitoring.mentionImporter.dateValidationFormatRequired');
                    }
                    if (
                        field.originalField.timeValidationFormatDropdownOptions
                        && (!field.selectedTimeValidationFormat || field.selectedTimeValidationFormat.value === null)
                    ) {
                        field.timeValidationFormatRequiredError = true;
                        errorMessage = errorMessage || this.localizationService.transform('Monitoring.mentionImporter.timeValidationFormatRequired');
                    }
                }
            });
        });
        if (this.parameters.reportDestinationEmails.length === 0) {
            errorMessage = errorMessage || this.localizationService.transform('Monitoring.mentionImporter.reportRecipient.required');
            this.noReportEmailError = true;
        }
        if (errorMessage) {
            this.messageService.add({
                severity: messageSeverityEnum.error,
                detail: errorMessage
            });
            FocusHelper.focusFirstError(this.el.nativeElement);
            return;
        }
        const [totalMentionCount, compatibleMentionCount, incompatibleFields, engagementErrors] = this.csvImportDialogService.setOutputModels();
        const incompatibleMentionCount = totalMentionCount - compatibleMentionCount;

        if (incompatibleFields.length > 0 && engagementErrors.incompatibleMentionCount > 0) {
            this.coreDialogService.open(ConfirmDialogComponent, {
                inputs: {
                    heading: 'Monitoring.mentionImporter.incompatibleConfirmation.heading',
                    messageHtml: this.localizationService.transform('Monitoring.mentionImporter.bothIncompatible.message', {
                        columns: incompatibleFields.join(','),
                    }),
                    list: Array.from(engagementErrors.incompatibleMediaTypes),
                    bottomMessage: this.localizationService.transform('Monitoring.mentionImporter.incompatibleConfirmation.bottomMessage', {
                        row: incompatibleMentionCount + engagementErrors.incompatibleMentionCount
                    }),
                    okLabel: compatibleMentionCount > 0 ? 'continue' : 'Core.button.confirm'
                },
                outputs: {
                    dialogClose: (confirm: boolean) => {
                        if (confirm && compatibleMentionCount > 0) {
                            this.showConfirmImportDialog();
                        }
                    }
                }
            });

        } else if (incompatibleFields.length > 0) {
            this.coreDialogService.open(ConfirmDialogComponent, {
                inputs: {
                    heading: 'Monitoring.mentionImporter.incompatibleConfirmation.heading',
                    message: 'Monitoring.mentionImporter.incompatibleConfirmation.message',
                    list: incompatibleFields,
                    bottomMessage: this.localizationService.transform('Monitoring.mentionImporter.incompatibleConfirmation.bottomMessage', {
                        row: incompatibleMentionCount
                    }),
                    okLabel: compatibleMentionCount > 0 ? 'continue' : 'Core.button.confirm'
                },
                outputs: {
                    dialogClose: (confirm: boolean) => {
                        if (confirm && compatibleMentionCount > 0) {
                            this.showConfirmImportDialog();
                        }
                    }
                }
            });
        } else if (engagementErrors.incompatibleMentionCount > 0) {
            this.coreDialogService.open(ConfirmDialogComponent, {
                inputs: {
                    heading: 'Monitoring.mentionImporter.incompatibleConfirmation.heading',
                    message: 'Monitoring.mentionImporter.incompatibleEngagement.message',
                    list: Array.from(engagementErrors.incompatibleMediaTypes),
                    bottomMessage: this.localizationService.transform('Monitoring.mentionImporter.incompatibleConfirmation.bottomMessage', {
                        row: engagementErrors.incompatibleMentionCount
                    }),
                    okLabel: compatibleMentionCount > 0 ? 'continue' : 'Core.button.confirm'
                },
                outputs: {
                    dialogClose: (confirm: boolean) => {
                        if (confirm && compatibleMentionCount > 0) {
                            this.showConfirmImportDialog();
                        }
                    }
                }
            });
        } else if (compatibleMentionCount === 0) {
            this.messageService.add({
                severity: messageSeverityEnum.error,
                detail: this.localizationService.transform(
                    'Monitoring.mentionImporter.noMentionsAvailable'
                )
            });
        } else {
            this.showConfirmImportDialog();
        }
    }

    onDropdownChanged(option: DropdownOptionModel, file: ImportedFile<CsvMentionApiModel, MentionImportFileParameters>, field): void {
        field.targetColumn = option.value;
        field.requiredError = false;
        field.dateValidationFormatRequiredError = false;
        field.timeValidationFormatRequiredError = false;
        this.csvImportDialogService.updateExampleValue(file, field);
    }

    selectDateTimeValidationFormat(field: CsvField<CsvMentionApiModel>) {
        this.csvImportDialogService.setDateTimeValidationFormat(field);
    }

    validateFallbackTextField(field: CsvField<CsvMentionApiModel>): void {
        field.requiredError = false;
        field.fallbackValueError = false;
        if (field.originalField.fallbackValueValidator && !field.originalField.fallbackValueValidator(field.fallbackValue)) {
            field.fallbackValueError = true;
        }
    }

    importFilesToCollection(collection: Folder | TagModel, collectionType: MentionImportToCollectionEnum): void {
        if (!this.fileParameters[collectionType].find(x => x.id === collection.id)) {
            this.fileParameters[collectionType].push(collection);
        }
        this.updateFileParameters();
    }

    removeCollection(collection: Folder | TagModel, collectionType: MentionImportToCollectionEnum) {
        const collectionList = this.fileParameters[collectionType];
        const index = collectionList.findIndex(x => x.name === collection.name);
        if (index > -1) {
            collectionList.splice(index, 1);
        }
    }

    private updateFileParameters(): void {
        this.csvFiles.forEach(file => {
            file.fileParameters = this.fileParameters;
        });
    }

    private isDateTimeField(field: CsvField<CsvMentionApiModel>): boolean {
        switch (field.originalField.fieldId) {
            case MentionImportTableFields.publishedDateTime:
            case MentionImportTableFields.publishedDate:
            case MentionImportTableFields.publishedTime:
                return true;
            default:
                return false;
        }
    }

    showConfirmImportDialog(): void {
        const fileName = this.selectedFiles[0]?.name ?? '';
        this.coreDialogService.open(MentionImportConfirmationComponent, {
            inputs: {
                files: this.selectedFiles,
                parameters: this.parameters,
                fileParameters: this.fileParameters
            },
            outputs: {
                dialogClose: (ok) => {
                    if (!ok) {
                        return;
                    }
                    if (this.mentionImportRequest) {
                        this.mentionImportRequest.unsubscribe();
                        this.mentionImportRequest = null;
                    }
                    let importDataRequest: Observable<MentionImportResponseModel[]>;
                    switch (this.selectedImportType.value) {
                        case MentionImportTypesEnum.GENERIC_CSV:
                        case MentionImportTypesEnum.CISION:
                            importDataRequest = this.csvImportDialogService.importData({ ...this.parameters, fileName });
                            break;
                        case MentionImportTypesEnum.NEWSDESK:
                            importDataRequest = this.newsdeskMentionImportService.importData({ ...this.fileParameters, fileName });
                            break;
                        case MentionImportTypesEnum.MELTWATER:
                            importDataRequest = this.meltwaterMentionImportService.importData({ ...this.fileParameters, fileName });
                            break;
                    }

                    this.loading = true;

                    this.mentionImportRequest = importDataRequest.pipe(
                        switchMap(response => {
                            if (this.selectedImportType.value === MentionImportTypesEnum.GENERIC_CSV) {
                                this.mentionCsvImportReportEmailService.sendEmailReport(
                                    this.parameters.reportDestinationEmails,
                                    this.selectedFiles.map(file => file.name),
                                    this.fileParameters.folders.map(folder => folder.name),
                                    this.fileParameters.tags.map(tag => tag.name),
                                    this.parameters.importToSystemCollection,
                                    this.csvFiles[0].csvFields,
                                    response
                                );
                            }
                            return of(null);
                        })
                    ).subscribe({
                        next: () => {
                            this.messageService.add({
                                severity: messageSeverityEnum.success,
                                summary: 'Monitoring.mentionImporter.complete.success',
                                detail: 'Monitoring.mentionImporter.complete.detail'
                            });

                            // if folder selected, reload LHP & current folder
                            if (this.fileParameters?.folders?.length) {
                                this.folderService.refreshAllManual().pipe(
                                    filter(x => !!x),
                                    take(1),
                                // eslint-disable-next-line rxjs/no-nested-subscribe
                                ).subscribe();
                                this.mentionSearchService.search(true, true);
                            }

                            // delay closing by 1 second
                            setTimeout(() => {
                                this.loading = false;
                                this.closeImporter();
                            }, 1000);
                        },
                        error: () => {
                            this.messageService.add({
                                severity: messageSeverityEnum.error,
                                summary: 'Monitoring.mentionImporter.error.header',
                                detail: 'Monitoring.mentionImporter.complete.detail'
                            });

                            this.loading = false;
                        }
                    });
                }
            }
        });
    }

    ngOnDestroy() {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
        if (this.mentionImportRequest) {
            this.mentionImportRequest.unsubscribe();
        }
    }
}
